import { ATLAS_AI_NOTE_WORKING } from '@common/constants/webSockets'
import { INotesData } from '@common/interfaces/notes'
import { IFileDownloadResponse } from '@common/interfaces/request'
import { Action } from 'redux'
import { takeEvery, put, select } from 'redux-saga/effects'
import { download } from '../../helpers/helpers'
import { putPromise } from '../../helpers/redux'
import { IArcNoteWorkingAction } from '../types'
import {
  EXPORT_NOTES_SUCCESS,
  EXPORT_NOTES_FAILURE,
  MARK_NOTES_READ_SUCCESS,
  countNotes,
  listNotes,
} from './actions'
import { notes as notesSelector, lastListContext as lastListContextSelector } from './selectors'

interface IMarkNotesReadSuccessAction {
  params?: { id?: string; workflow?: string }
  data?: { markedRead?: number }
}

export function* exportNotes() {
  yield takeEvery(EXPORT_NOTES_SUCCESS, function* (action: IFileDownloadResponse & Action) {
    try {
      const data = action.data
      const fileName = data.name
      yield download(data, fileName)
    } catch (error) {
      yield put({ type: EXPORT_NOTES_FAILURE, error: error as Error })
    }
  })
}

// When Arc finishes working a note thread it was @-tagged in
// (ATLAS_AI_NOTE_WORKING → isArcWorking:false), silently refetch the loaded
// notes list so Arc's freshly-posted reply appears without a manual reload.
//
// The note-viewing user isn't a member of Arc's hidden session, so the
// session-scoped ATLAS_AI_COMPLETE refetch (atlasai/sagas.ts:handleArcWriteComplete)
// never reaches them — this note-scoped event is their only "answer landed"
// signal. Reuses `lastListContext` (the entity id + params of the most recent
// non-loadMore fetch) so the exact same page reloads.
//
// Preserves in-progress drafts: the refetch only replaces redux note data, and
// each note/editor is keyed by note id (stable across the refetch), so an open
// reply or edit editor keeps its local Draft.js state. `skipLoader` avoids a
// skeleton flash. Only fires when a list is actually loaded on screen.
export function* refetchNotesOnArcComplete() {
  yield takeEvery(ATLAS_AI_NOTE_WORKING, function* (action: IArcNoteWorkingAction) {
    if (action.data?.isArcWorking) return
    const loadedNotes = (yield select(notesSelector)) as INotesData | null
    const ctx = (yield select(lastListContextSelector)) as {
      id?: string
      params?: object
    } | null
    if (!loadedNotes || !ctx?.id) return
    // ATLAS_AI_NOTE_WORKING fans out to every internal user, so only refetch when
    // the completed thread's root note is actually in this user's loaded list —
    // otherwise an Arc run on an unrelated client/entity would refetch everyone's
    // list system-wide.
    if (!loadedNotes.data?.some((note) => note.id === action.data.noteId)) return
    yield putPromise(listNotes(ctx.id, { ...ctx.params, loadMore: false, skipLoader: true }))
  })
}

// The reducer's decrement by `markedRead` is only an estimate of the rail badge: it includes
// replies, and cell notes from other surfaces a diligence drawer shows but the badge doesn't
// count. Re-read the real count so the badge settles on the server's number.
export function* refreshNotesCountOnRead() {
  yield takeEvery(
    MARK_NOTES_READ_SUCCESS,
    function* (action: Action & IMarkNotesReadSuccessAction) {
      const { id, workflow } = action.params ?? {}
      if (!id || !workflow || !action.data?.markedRead) return
      yield putPromise(countNotes(id, { workflow }))
    },
  )
}
